import { Link, createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { useId, type ReactNode } from 'react'
import { z } from 'zod'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import { SECTION_CARD } from '@/components/ui/section-card-variants'
import Skeleton from '@/components/ui/Skeleton'
import StatStrip, { type StatStripItem } from '@/components/ui/StatStrip'
import StockAvatar from '@/components/ui/StockAvatar'
import TabBar, { TabPanel } from '@/components/ui/TabBar'
import TitleRow from '@/components/ui/TitleRow'
import {
  useFundDividends,
  useFundHoldings,
  useFundIncomeStatement,
  useFundNav,
  useFundStockCount,
  useSuspenseFundDirectory,
} from '@/features/mutual-funds/api/queries'
import { fundInitials, fundSubtitle } from '@/features/mutual-funds/lib/format'
import { EM_DASH, formatPercentValue, formatRatio, formatRupees } from '@/lib/broker-format'
import { formatDate, formatQuantity } from '@/lib/format'
import { sign, toChartNumber } from '@/lib/money'
import type { Fund, OpenEndedDividend } from '@/schemas/mutual-funds'
import FundHoldingsTable from '@/screens/mutual-funds/components/fund/FundHoldingsTable'
import FundIncomeTab from '@/screens/mutual-funds/components/fund/FundIncomeTab'
import FundNavTab from '@/screens/mutual-funds/components/fund/FundNavTab'
import FundSectorBars from '@/screens/mutual-funds/components/fund/FundSectorBars'
import FundStockMonths from '@/screens/mutual-funds/components/fund/FundStockMonths'
import {
  formatMaturityDate,
  maturityLabel,
} from '@/screens/mutual-funds/components/price-vs-nav/format'
import { fundSymbolOf, loadFund } from '@/screens/mutual-funds/FundScreen.loader'

const BACK = '/mutual-funds/rankings'

/** Built from the loaded page's own boxes: the head, the four-figure strip and the pair. */
function FundPending() {
  return (
    <ScreenLayout pending pendingLabel="Loading the fund…" pageTitle="Fund">
      <div className="flex items-center gap-4">
        <Skeleton className="size-12 rounded-full" />
        <div className="flex flex-col gap-2">
          <Skeleton className="h-6 w-32 rounded-xs" />
          <Skeleton className="h-4 w-64 rounded-xs" />
        </div>
      </div>
      <Skeleton className="h-22 w-full rounded-lg" />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
        <Skeleton className="h-80 w-full rounded-lg" />
        <Skeleton className="h-80 w-full rounded-lg" />
      </div>
    </ScreenLayout>
  )
}

function FundError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} title="This fund did not load" />
}

/** The mock's three tabs: Essentials always, NAV and Income Statement when the fund has them. */
const FUND_TABS = ['essentials', 'nav', 'income'] as const
type FundTab = (typeof FUND_TABS)[number]

const TAB_LABELS: Record<FundTab, string> = {
  essentials: 'Essentials',
  nav: 'NAV',
  income: 'Income Statement',
}

const fundTabSchema = z.enum(FUND_TABS)

/** How many of the fund's stocks "Largest holdings" lists, as the mock does. */
const LARGEST_HOLDINGS = 10

export const Route = createFileRoute('/_app/mutual-funds/fund/$symbol')({
  validateSearch: z.object({ tab: fundTabSchema.optional().catch(undefined) }),
  loader: loadFund,
  pendingComponent: FundPending,
  errorComponent: FundError,
  component: FundScreenRoute,
})

function FundScreenRoute() {
  const symbol = fundSymbolOf(Route.useParams().symbol)
  // `key`: another fund's page is a new page, not this one's blocks re-keyed.
  return <FundScreen key={symbol} symbol={symbol} />
}

function FundNotFound() {
  return (
    <ScreenLayout pageTitle="Fund not found">
      <EmptyState
        kind="not-found"
        size="page"
        headingLevel="h1"
        title="Fund not found"
        description="No mutual fund goes by this symbol, or the link is wrong."
        action={
          <Link to={BACK} className={stateActionClass('page')}>
            See all funds
          </Link>
        }
      />
    </ScreenLayout>
  )
}

type FundKind = 'Open-ended' | 'Close-ended' | 'Matured'

const kindOf = (fund: Fund): FundKind =>
  fund.status === 'MATURED'
    ? 'Matured'
    : fund.fundType === 'OPEN_ENDED'
      ? 'Open-ended'
      : 'Close-ended'

const NOT_GIVEN = 'Not given'

/** A fund's dividend record: only open-ended funds have one, and `"0.00"` means none paid yet. */
const paidRecord = (record: OpenEndedDividend | undefined): OpenEndedDividend | undefined =>
  record?.totalPercent != null && toChartNumber(record.totalPercent) > 0 ? record : undefined

/** "5.2% below NAV" / "3.1% above NAV" / "at NAV", from the directory's premium/discount. */
const againstNav = (fund: Fund): string | undefined => {
  const percent = fund.premiumDiscountPercent
  if (percent === null) return undefined
  const direction = sign(percent)
  if (direction === 0) return 'at NAV'
  return `${Math.abs(toChartNumber(percent)).toFixed(1)}% ${direction < 0 ? 'below' : 'above'} NAV`
}

const NAV_TERM = {
  title: 'NAV (net asset value)',
  body: 'What one unit of the fund is worth: everything the fund owns, less what it owes, divided by its units.',
}

const DIVIDEND_TERM = {
  title: 'Dividend each year',
  body: 'The fund’s total dividend so far, spread evenly over the years it has paid, as a share of each unit’s Rs 10 face value.',
}

/**
 * The strip (the mock's `mf-fd__stats`): NAV, then for a listed fund its market price against
 * NAV and its size, else its size and how many stocks it holds; last its dividend each year (an
 * open-ended fund that has paid), when it matured, or when it matures.
 */
const statsOf = (
  fund: Fund,
  kind: FundKind,
  stocks: number | undefined,
  dividend: OpenEndedDividend | undefined,
): StatStripItem[] => {
  const size: StatStripItem = {
    label: 'Fund size',
    value: fund.fundSize === null ? NOT_GIVEN : formatRupees(fund.fundSize),
  }
  const listed = fund.isListed && fund.marketPrice !== null
  const items: StatStripItem[] = [
    {
      label: 'NAV',
      term: NAV_TERM,
      value: fund.nav === null ? NOT_GIVEN : `Rs ${formatRatio(fund.nav)}`,
      ...(fund.navPeriod === null ? {} : { sub: `As of ${fund.navPeriod}` }),
    },
  ]
  if (listed && fund.marketPrice !== null) {
    const words = againstNav(fund)
    items.push(
      {
        label: 'Market price',
        value: `Rs ${formatRatio(fund.marketPrice)}`,
        ...(words === undefined ? {} : { sub: words }),
      },
      size,
    )
  } else {
    items.push(size, {
      label: 'Stocks held',
      value: stocks === undefined ? EM_DASH : formatQuantity(stocks),
    })
  }
  if (dividend !== undefined) {
    items.push({
      label: 'Dividend each year',
      term: DIVIDEND_TERM,
      value: formatPercentValue(dividend.perYearPercent),
      sub: `${formatPercentValue(dividend.totalPercent)} in all`,
    })
  } else if (kind === 'Matured') {
    items.push({ label: 'Matured', value: formatMaturityDate(fund.maturityDate) ?? NOT_GIVEN })
  } else {
    items.push({
      label: 'Matures',
      value:
        formatMaturityDate(fund.maturityDate) ??
        (kind === 'Open-ended' ? 'No end date' : NOT_GIVEN),
    })
  }
  return items
}

/** A labelled fact in "About this fund": 12 tertiary over 14, 4 apart. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <dt className="text-body-xs text-muted-foreground-subtle">{label}</dt>
      <dd className="text-body-m">{children}</dd>
    </div>
  )
}

function AboutFund({
  fund,
  kind,
  dividend,
}: {
  fund: Fund
  kind: FundKind
  dividend: OpenEndedDividend | undefined
}) {
  const listedWords =
    kind === 'Close-ended' ? (fund.isListed ? ', listed on NEPSE' : ', not listed yet') : ''
  return (
    <section aria-labelledby="fund-about" className={SECTION_CARD}>
      <h2 id="fund-about" className="text-h-m">
        About this fund
      </h2>
      <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
        <Fact label="Kind">
          {kind}
          {listedWords}
        </Fact>
        <Fact label="Fund manager">{fund.fundManager ?? NOT_GIVEN}</Fact>
        <Fact label="Opened">
          {fund.startDate === null ? NOT_GIVEN : formatDate(fund.startDate)}
        </Fact>
        <Fact label="Runs for">
          {maturityLabel(fund.maturityYears) ?? (kind === 'Open-ended' ? 'No end date' : NOT_GIVEN)}
        </Fact>
        <Fact label="NEPSE when it opened">
          <span className="tnum">
            {fund.nepseIndexAtStart === null ? NOT_GIVEN : formatRatio(fund.nepseIndexAtStart)}
          </span>
        </Fact>
        {/* Only open-ended funds have a dividend record (`/dividends/`); a close-ended fund's
            "None recorded" would be a claim no endpoint backs. */}
        {kind === 'Open-ended' ? (
          <Fact label="Total dividend">
            {dividend === undefined ? 'None recorded' : formatPercentValue(dividend.totalPercent)}
          </Fact>
        ) : null}
      </dl>
    </section>
  )
}

/**
 * Mutual funds › one fund (2026-11-03 mock `/funds/fund?s=`, no Figma frame): its mark, symbol
 * and name (kind, manager); a strip of NAV, price or size, stocks and dividend; then where its
 * money is beside its stocks held month by month, and the facts about it.
 *
 * Built from several reads, since mm-be has no one-fund endpoint: the directory row (the page's
 * gate, from the loader), the stock count, the sector split, the trends line, the dividend
 * record, and (mm-be #372, #376) the fund's own holdings, NAV history and income statement.
 * Every block past the strip waits on its own read.
 *
 * Those three reads answer 404 until mm-be's feat/web-asks-funds-portfolio is deployed, read as
 * `null`: Largest holdings then does not draw, and a tab is drawn only for data the fund has (NAV
 * filings, a published year). With Essentials alone there is no strip at all.
 */
function FundScreen({ symbol }: { symbol: string }) {
  const { data: funds } = useSuspenseFundDirectory()
  const stockCount = useFundStockCount(null)
  const dividends = useFundDividends()
  const holdings = useFundHoldings(symbol, null)
  const nav = useFundNav(symbol)
  const income = useFundIncomeStatement(symbol)
  const { tab } = Route.useSearch()
  const navigate = Route.useNavigate()
  const idBase = useId()

  const fund = funds.find((row) => row.symbol === symbol)
  if (fund === undefined) return <FundNotFound />

  const kind = kindOf(fund)
  const stocks = stockCount.data?.find((row) => row.symbol === symbol)?.stockCount
  const dividend = paidRecord(dividends.data?.openEnded.find((row) => row.symbol === symbol))
  const largest = holdings.data?.companies.slice(0, LARGEST_HOLDINGS) ?? []
  const navData = nav.data != null && nav.data.points.length > 0 ? nav.data : null
  const incomeData = income.data != null && income.data.years.length > 0 ? income.data : null
  const tabs = FUND_TABS.filter(
    (candidate) =>
      candidate === 'essentials' ||
      (candidate === 'nav' && navData !== null) ||
      (candidate === 'income' && incomeData !== null),
  )
  const activeTab: FundTab = tab !== undefined && tabs.includes(tab) ? tab : 'essentials'

  const essentials = (
    <>
      {/* Two columns only when the month chart drew: alone, the sector split takes the row. */}
      <div className="grid items-start gap-4 lg:[&:has(>:nth-child(2))]:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
        <FundSectorBars symbol={symbol} />
        <FundStockMonths symbol={symbol} />
      </div>

      {holdings.data == null || largest.length === 0 ? null : (
        <section aria-labelledby="fund-holdings" className="flex flex-col gap-3">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <h2 id="fund-holdings" className="text-h-m">
              Largest holdings
            </h2>
            <p className="text-body-xs text-muted-foreground-subtle">
              Top {largest.length} of {holdings.data.stockCount} stocks
              {holdings.data.period === null ? '' : `, ${holdings.data.period}`}
            </p>
          </div>
          <FundHoldingsTable fund={symbol} rows={largest} />
        </section>
      )}

      <AboutFund fund={fund} kind={kind} dividend={dividend} />
    </>
  )
  const name = fundSubtitle(symbol, fund.name)
  const manager = fund.fundManager === null ? '' : ` · Managed by ${fund.fundManager}`

  return (
    <ScreenLayout
      header={
        <TitleRow
          title={symbol}
          back={BACK}
          leading={
            <StockAvatar
              symbol={symbol}
              monogram={fundInitials(symbol)}
              size="xl"
              className="text-muted-foreground-subtle"
            />
          }
          subtitle={
            <>
              {name === null ? null : `${name} `}
              <span className="text-muted-foreground-subtle">
                · {kind}
                {manager}
              </span>
            </>
          }
        />
      }
      pageTitle={fund.name ?? symbol}
    >
      <div className="flex flex-col gap-3">
        <StatStrip items={statsOf(fund, kind, stocks, dividend)} />
        {fund.isListed && fund.marketPrice !== null ? (
          <p className="text-body-xs text-muted-foreground-subtle">
            A listed close-ended fund trades like a share, so its price can sit above or below what
            each unit is worth (NAV).
          </p>
        ) : null}
      </div>

      {tabs.length === 1 ? (
        essentials
      ) : (
        <div className="flex flex-col gap-4">
          <TabBar
            options={tabs.map((value) => ({ value, label: TAB_LABELS[value] }))}
            value={activeTab}
            onChange={(next) => {
              const picked = fundTabSchema.catch('essentials').parse(next)
              void navigate({
                search: { tab: picked === 'essentials' ? undefined : picked },
                replace: true,
              })
            }}
            ariaLabel="Fund sections"
            idBase={idBase}
          />
          <TabPanel idBase={idBase} value={activeTab} className="flex flex-col gap-6">
            {activeTab === 'nav' && navData !== null ? (
              <FundNavTab nav={navData} />
            ) : activeTab === 'income' && incomeData !== null ? (
              <FundIncomeTab statement={incomeData} />
            ) : (
              essentials
            )}
          </TabPanel>
        </div>
      )}
    </ScreenLayout>
  )
}
